import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import EditDocumentDialog from "../components/EditDocumentDialog";
import PreviewDialog from "../components/PreviewDialog";
import { fmtDate } from "../lib/utils-app";
import { useAuth } from "../context/AuthContext";
import { Search as SearchIcon, Download, Pencil, FileSearch, RefreshCw, Eye } from "lucide-react";
import { toast } from "sonner";

const ENTITY_LABELS = { company: "Azienda", worker: "Lavoratore", site: "Cantiere", equipment: "Attrezzatura" };
const ENTITY_PATH = { company: "/aziende", worker: "/lavoratori", site: "/cantieri", equipment: "/attrezzature" };

function Highlight({ text, q }) {
  if (!text) return null;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return <>{parts.map((p, i) => p.toLowerCase() === q.toLowerCase() ? <mark key={`${i}-${p}`} className="bg-amber-200 rounded px-0.5">{p}</mark> : p)}</>;
}

export default function SearchPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState("");
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);

  const run = async (term) => {
    const t = (term ?? q).trim();
    if (t.length < 2) { toast.error("Inserisci almeno 2 caratteri"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/search", { params: { q: t } });
      setItems(data); setSearched(t); setParams({ q: t });
    } catch { toast.error("Errore ricerca"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (params.get("q")) run(params.get("q")); }, []); // eslint-disable-line

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const reindex = async () => {
    try { const { data } = await api.post("/admin/reindex-documents"); toast.success(`${data.queued} documenti in indicizzazione`); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Archivio</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><FileSearch size={28} /> Ricerca full-text</h1>
          <p className="text-slate-500 mt-1">Trova documenti cercando parole contenute al loro interno (testo PDF e OCR).</p>
        </div>
        {user?.role === "admin" && (
          <button onClick={reindex} data-testid="reindex-button" className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-lg text-sm">
            <RefreshCw size={14} /> Indicizza documenti esistenti
          </button>
        )}
      </header>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" placeholder="Es. codice fiscale, nome corso, matricola, numero DURC…"
            className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg text-base bg-white" />
        </div>
        <button type="submit" disabled={busy} data-testid="search-submit" className="px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium disabled:opacity-60">
          {busy ? "Ricerca…" : "Cerca"}
        </button>
      </form>

      {searched && (
        <div className="text-sm text-slate-500" data-testid="search-count">{items.length} risultati per «{searched}»</div>
      )}

      <div className="space-y-3">
        {items.map(d => (
          <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-4 justify-between" data-testid="search-result">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-mono uppercase tracking-wider text-blue-700">{d.categoria}</span>
                <StatusBadge status={d.status} />
              </div>
              <div className="font-display font-semibold text-slate-900 mt-1"><Highlight text={d.nome} q={searched} /></div>
              <div className="text-xs text-slate-500 font-mono">{d.original_filename}</div>
              {d.snippet && <p className="text-sm text-slate-700 mt-2 bg-slate-50 border-l-2 border-amber-400 pl-3 py-1"><Highlight text={d.snippet} q={searched} /></p>}
              <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-3">
                <Link to={`/aziende/${d.company_id}`} className="text-blue-700 hover:underline">{d.company_name}</Link>
                <span>{ENTITY_LABELS[d.entity_type]}: {d.entity_type === "company" ? d.company_name : <Link to={`${ENTITY_PATH[d.entity_type]}/${d.entity_id}`} className="text-blue-700 hover:underline">{d.entity_name}</Link>}</span>
                <span>Scadenza: {fmtDate(d.data_scadenza)}</span>
              </div>
            </div>
            <div className="flex items-start gap-1">
              <button onClick={() => setPreview(d)} data-testid={`preview-doc-${d.id}`} className="text-slate-700 p-1.5" title="Anteprima"><Eye size={16} /></button>
              <button onClick={() => download(d)} className="text-blue-700 p-1.5" title="Scarica"><Download size={16} /></button>
              <button onClick={() => setEdit(d)} className="text-slate-700 p-1.5" title="Modifica"><Pencil size={16} /></button>
            </div>
          </div>
        ))}
        {searched && items.length === 0 && !busy && (
          <div className="border border-dashed border-slate-300 rounded-lg py-10 text-center text-slate-500">Nessun documento contiene «{searched}».</div>
        )}
      </div>

      {edit && <EditDocumentDialog doc={edit} onClose={() => setEdit(null)} onSaved={() => run(searched)} />}
      {preview && <PreviewDialog doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
